import BaseFilterResponse from './base-filter-response';
import FilterConstants from '../../filter-constants';
import MathUtils from '../utils/math';
import * as StageUtils from '../utils/stage';
import { filters } from '../../models/filter/filter';

export default class Butterworth extends BaseFilterResponse {
  name = FilterConstants.responseType.butterworth;

  /**
    @function calculateButterworthPoleFrequency
    @param passbandAttenuation {int} passband attenuation from the user in dB; always a negative number
    @param order {int} returned order from calculate_order function
    @param passbandFrequency {int} passband frequency from the user in Hz
    @description takes in passband attenuation, order as calculated and the passband frequency to calculate pole frequency
    @returns butterworth pole frequency(fp)
    */
  calculateButterworthPoleFrequency(negPassbandAttenuation, order, passbandFrequency) {
    //need positive numbers for formula to work (turn negatives to positive)
    const passbandAttenuation = negPassbandAttenuation * -1;

    var first = 0.1 * passbandAttenuation;
    var fp = Math.pow(10, first) - 1;
    fp = Math.sqrt(fp);
    var fpPower = -1 / order;
    fp = Math.pow(fp, fpPower);
    fp = fp * passbandFrequency;

    return fp;
  }

  butterworthStagesGivenOrderAndCutoff(orderNum, fp) {
    //Butterworth pole angles of 2nd order stages
    var poleAngles = this._calculateButterworthPoleAngles(orderNum);

    //Q of second order stages
    var qArray = this._calculateButterworthQ(poleAngles);

    //build array of orders
    var loopCount = Math.floor(orderNum / 2);
    var result = [];
    var stageId = 1;

    //check if the order is odd

    if (orderNum % 2 !== 0) {
      var firstOrder = {
        stageQ: Math.sqrt(2) / 2,
        stageFp: fp,
        stageFz: 1,
        stageId: stageId,
        filter: filters[FilterConstants.filterType.lowPass], //LowPass.create(),
        orderType: 1,
        gain: 1,
      };
      result.push(firstOrder);
      stageId++;
    }

    for (var k = 0; k < loopCount; k++) {
      var secondOrder = {
        stageQ: qArray[k],
        stageFp: fp,
        stageFz: 1,
        stageId: stageId,
        filter: filters[FilterConstants.filterType.lowPass], //LowPass.create(),
        orderType: 2,
        gain: 1,
      };
      result.push(secondOrder);
      stageId++;
    }

    return result;
  }

  /**
    Butterworth Algorithm
    @function Butterworth_lowpass - This is a Butterworth filter algorithm.
    @param {int}  passbandAttenuation - passband attenuation from the user in dB; always a negative number
    @param {int}  stopbandAttenuation - stopband attenuation from the user in dB; always a negative number
    @param {int}  passbandFrequency - passband frequency from the user in Hz
    @param {int}  stopbandFrequency - stopband frequency from the user in Hz
    @description This function calculates the butterworth filter response
    @returns {array} stageObjects array of stageObjects containing order,fp,Q,fz, and gain properties
    */
  lowPass(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency) {
    var result = null;

    //find filter order
    var orderNum = this.getOrder(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency);
    if (orderNum <= FilterConstants.maxOrder) {
      //get the 3dB cutoff frequency
      var fp = this.calculateButterworthPoleFrequency(passbandAttenuation, orderNum, passbandFrequency);
      //now get the stages
      var stageValues = this.butterworthStagesGivenOrderAndCutoff(orderNum, fp);
      result = StageUtils.createTargetStages(stageValues);
    } else {
      throw 'Cannot go beyond 5 stages';
    }

    return result;
  }

  /**
    @function getOrder
    @param passbandAttenuation {int} passband attenuation in dB; always a negative number
    @param stopbandAttenuation {int} stopband attenuation in dB; always a negative number
    @param passbandFrequency {int} passband frequency in Hz
    @param stopbandFrequency {int} stopband frequency in Hz
    @description takes in passband attenuation, stopband attenuation, passband frequency, and stopband frequency to figure out order
    @returns butterworth order
    */
  getOrder(negPassbandAttenuation, negStopbandAttenuation, passbandFrequency, stopbandFrequency) {
    //need positive numbers for formula to work (turn negatives to positive)
    const passbandAttenuation = negPassbandAttenuation * -1;
    const stopbandAttenuation = negStopbandAttenuation * -1;

    var nFractional, order;
    var innerNumerator = 0.1 * stopbandAttenuation;
    var innerDenominator = 0.1 * passbandAttenuation;

    var innerNumerator1 = Math.pow(10, innerNumerator) - 1;
    var innerDenominator1 = Math.pow(10, innerDenominator) - 1;

    var logTermTop = innerNumerator1 / innerDenominator1;
    logTermTop = MathUtils.logBaseTen(logTermTop);

    var logTermBottom = stopbandFrequency / passbandFrequency;

    nFractional = 2 * MathUtils.logBaseTen(logTermBottom);

    //Get nFractional
    nFractional = logTermTop / nFractional;

    //round nFractional to next integer up to get order
    order = Math.ceil(nFractional);

    return order;
  }

  /**
    @function calculateQ
    @param poleAnglesArray {array} poleAngles of 2nd order
    @returns Q {array} Q Array
    @description calculates Q from thetaK within poleAnglesArray
    */
  _calculateButterworthQ(poleAnglesArray) {
    var resultQ = [];

    //loop through thetaKArray perform QoK = 1/ 2*cos(thetaK)
    for (var i = 0; i < poleAnglesArray.length; i++) {
      var stepOne = 2 * Math.cos(poleAnglesArray[i]);
      var stepTwo = 1 / stepOne;
      resultQ.push(stepTwo);
    }

    return resultQ;
  }

  /**
    @function calculatePoleAngles
    @param order {int} order of 2nd order stage for butterworth
    @returns {array} [order,thetaK]
    @description pushes Butterworth pole angles of 2nd order stages
    */
  _calculateButterworthPoleAngles(order) {
    var thetaK,
      result = [],
      thetaKrad;

    for (var k = 1; k <= Math.floor(order / 2); k++) {
      var stepOne = 2 * k + order - 1;
      var stepTwo = stepOne / order;
      var stepThree = 90 * stepTwo;
      var stepFour = stepThree - 180;
      thetaK = stepFour;
      thetaKrad = thetaK * (Math.PI / 180); // converting thetaK into radians
      result.push(thetaKrad);
    }

    return result;
  }
}
